<template>
  <section class="height-100-p">
    <el-table
      :data="outboundUnits.list"
      v-loading="outboundUnits.loadingSearch"
      id="outbound-units-data-list"
      size="mini"
      tooltip-effect="dark"
      class="dark-blue-table"
      :empty-text="$t('common.notAbleToFindRecords')"
      :default-sort = "{prop: 'unitId', order: 'descending'}"
      sortable
      stripe
      @sort-change="handleSortChange"
      @selection-change="handleSelection"
    >
      <el-table-column
        prop="unitId"
        :label="$t('outbound.units.unitID')"
        :min-width="10"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.unitId" placement="top">
            <span> {{ scope.row.unitId }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="warehouseCode"
        :label="$t('outbound.units.wh')"
        :min-width="4"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="shipDate"
        :label="$t('outbound.units.dos')"
        :min-width="8"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="shipperAccountName"
        :label="$t('outbound.units.shipper')"
        :min-width="11"
        align="left"
        header-align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.shipperAccountName" placement="top">
            <span> {{ scope.row.shipperAccountName }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="consigneeAccountName"
        :label="$t('outbound.units.consignee')"
        :min-width="11"
        align="left"
        header-align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.consigneeAccountName" placement="top">
            <span> {{ scope.row.consigneeAccountName }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="palletCode"
        :label="$t('outbound.units.rPallet')"
        :min-width="8"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.palletCode" placement="top">
            <span> {{ scope.row.palletCode }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="putAwayCode"
        :label="$t('outbound.units.putAwayID')"
        :min-width="8"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.putAwayCode" placement="top">
            <span> {{ scope.row.putAwayCode }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="trailer"
        :label="$t('outbound.units.trailer')"
        :min-width="11"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.trailer" placement="top">
            <span> {{ scope.row.trailer }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="damaged"
        :label="$t('outbound.units.dmg')"
        :min-width="4"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <span> {{ damagedLabel(scope.row.damaged) }} </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="temperature"
        :label="$t('outbound.units.temp')"
        :min-width="5"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="receivedTime"
        :label="$t('outbound.units.timeReceived')"
        :min-width="9"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.receivedTime" placement="top">
            <span> {{ scope.row.receivedTime }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="loadedTime"
        :label="$t('outbound.units.timeLoaded')"
        :min-width="9"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.loadedTime" placement="top">
            <span> {{ scope.row.loadedTime }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        :label="$t('outbound.units.status')"
        :min-width="6"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <span> {{ statusLabel(scope.row.status, (scope.row.shuttleLoadId && scope.row.shuttleLoadId > 0)) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        width="60"
        :label="$t('common.options')"
        align="center"
        class-name="fixed-width"
        v-if="!outboundUnits.multiSelect"
      >
        <template slot-scope="scope">
          <unit-detail-dialog :unit-entity-id="scope.row.id" />
          <print-unit-label :unitId="scope.row.unitId" :labelPrinted.sync="scope.row.labelPrinted" />
          <el-tooltip
            v-if="canDeleteUnit(scope.row)"
            class="item"
            effect="dark"
            :content="$t('common.delete')"
            placement="top-start"
          >
            <i class="el-icon-delete icon-action" @click="confirmationMessage(scope.row)" />
          </el-tooltip>
          <el-tooltip
            v-if="canReactivateUnit(scope.row)"
            class="item"
            effect="dark"
            :content="$t('common.reactivate')"
            placement="top-start"
          >
            <i class="el-icon-refresh-left icon-action" @click="confirmationReactivateMessage(scope.row)" />
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        v-for="i in (outboundUnits.multiSelect) ? [1]: []" :key="i"
        width="60"
        type="selection"
        align="center"
        class-name="fixed-width"
      ></el-table-column>
    </el-table>
    <div class="pagination-footer" v-show="outboundUnits.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('common.total') }}</span>
           {{ outboundUnits.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="outboundUnits.actualFilters.rows"
        :current-page.sync="outboundUnits.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="outboundUnits.total"
        layout="sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import constants from '@/utils/constants';
import unitDataTransformLabels from '@/plugins/unitDataTransformLabels';
import PrintUnitLabel from '@/views/customerService/orderEntry/components/unitsDetail/PrintUnitLabel';
import {
  ORDER_ENTRY_ALREADY_RECEIVED_UNIT,
  ORDER_ENTRY_DELETE_UNIT,
  ORDER_ENTRY_REACTIVATE_UNIT,
  OUTBOUND_UNITS_SEARCH,
  OUTBOUND_UNITS_SET_SELECTED_UNITS
} from '@/store/actions';
import UnitDetailDialog from './components/UnitDetailDialog';

export default {
  components: { UnitDetailDialog, PrintUnitLabel },
  name: 'OutboundUnitsSummarySection',
  mixins: [unitDataTransformLabels],
  computed: {
    ...mapGetters(['outboundUnits']),
    sizes: () => constants.TABLES.DEFAULT_LIMIT_SIZES,
  },
  methods: {
    ...mapActions({
      search: OUTBOUND_UNITS_SEARCH,
      alreadyReceivedUnit: ORDER_ENTRY_ALREADY_RECEIVED_UNIT,
      deleteUnit: ORDER_ENTRY_DELETE_UNIT,
      reactivateUnit: ORDER_ENTRY_REACTIVATE_UNIT,
      setSelectedUnits: OUTBOUND_UNITS_SET_SELECTED_UNITS,
    }),
    handleSearchChangeLimit(val) {
      this.search({ rows: val, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(val) {
      this.search({ page: val });
    },
    handleSortChange({ prop, order }) {
      this.search({ orderField: prop, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
    async confirmationMessage(row) {
      let message = this.$t('warehouse.orderEntry.unitDeleteConfirmation')
        .replace('[id]', row.unitId).replace('[shipDate]', row.shipDate);

      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      const alreadyReceivedUnit = await this.alreadyReceivedUnit(row.unitId);
      loading.close();

      if (alreadyReceivedUnit && !this.$can(this.permissions.DATA_ENTRY.DELETE_AFTER_RECEIVED)) {
        this.$message.error(this.$t('warehouse.orderEntry.unitDeleteError'));
        return;
      }

      if (alreadyReceivedUnit && this.$can(this.permissions.DATA_ENTRY.DELETE_AFTER_RECEIVED)) {
        message = this.$t('warehouse.orderEntry.receivedUnitDeletionWarning').concat(message);
      }

      const confirm = await this.$confirm(message, { confirmButtonText: this.$t('common.yes'), dangerouslyUseHTMLString: true }).catch(() => false);
      if (confirm) {
        await this.handleDelete(row);
      }
    },
    async handleDelete(row) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        const response = await this.deleteUnit(row.unitId);
        this.$message.success(response.message);
        this.search({});
      } finally {
        loading.close();
      }
    },
    async handleReactivate(row) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        const response = await this.reactivateUnit(row.id);
        this.$message.success(response.message);
        this.search({});
      } finally {
        loading.close();
      }
    },
    async confirmationReactivateMessage(row) {
      const message = this.$t('warehouse.orderEntry.reactivateUnitConfirmation').replace('[unit id]', row.unitId);

      const confirm = await this.$confirm(message, { confirmButtonText: this.$t('common.yes'), dangerouslyUseHTMLString: true }).catch(() => false);
      if (confirm) {
        await this.handleReactivate(row);
      }
    },
    canDeleteUnit(row) {
      return row.active
        && (this.$can(this.permissions.DATA_ENTRY.DELETE_AFTER_CUTOFF_FINALIZED, row.status)
        || this.$can(this.permissions.DATA_ENTRY.DELETE_AFTER_CUTOFF_PENDING, row.status)
        || this.$can(this.permissions.DATA_ENTRY.DELETE_BEFORE_CUTOFF_FINALIZED, row.status)
        || this.$can(this.permissions.DATA_ENTRY.DELETE_BEFORE_CUTOFF_PENDING, row.status)
        || this.$can(this.permissions.DATA_ENTRY.DELETE_AFTER_RECEIVED, row.status));
    },
    canReactivateUnit(row) {
      return !row.active
        && (this.$can(this.permissions.DATA_ENTRY.REACTIVATE_UNIT));
    },
    handleSelection(selection) {
      this.setSelectedUnits(selection);
    },
  }
};
</script>
